import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import BigButton from "components/atoms/buttons/BigButton";
import {
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import Spinner from "components/atoms/loader/Spinner";
import StepperComponent from "components/blocks/stepper/Stepper";
import {
  AccountInfo,
  CreateAccountRequest,
  PricingRequest,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { AddOrgPricing } from "./AddOrgPricing";
import { DISCOUNT_TIER } from "api/interfaces/subscription.interface";
import { addOrgSteps } from "utils/AccountUtil";
import { AccountInner } from "../detail/AccountInner";
import { toTrim } from "utils/functions";

export enum UPDATE_TYPE {
  NAME = 0,
  LEVEL = 1,
  TIME_ZONE = 2,
  DATE_FORMAT = 3,
  TIME_FORMAT = 4,
  ADDITIONAL_ACCOUNT_NUMBER = 5,
  PRICING = 6,
  TWO_FACTOR_AUTH = 7,
  INVITE_NAME = 8,
  INVITE_EMAIL = 9,
  INVITE_PHONE_NUMBER = 10,
  ACCOUNT_NUMBER = 11,
  BILLING_TYPE = 12,
}

interface Props {
  accountInfo: CreateAccountRequest;
  isToggled: boolean;
  onChange: () => void;
  onModal: (type?: string) => void;
  isFlagSelected: string;
  accountNumber: string;
  accountNumberPrefix: string;
  onSelectFlag: (code: string) => void;
  onClickCreate: () => void;
  onChageAccountInfo: (
    type: UPDATE_TYPE,
    value: string | number | boolean
  ) => void;
  accountValidate: Validate;
  accountValidateMsg: ValidateMsg;
  parentLevel: LEVEL_TYPE;
  currentLevel: string;
  isCreationLoading: boolean;
  isCreateDisabled: boolean;

  onChangePage: (name: string, index?: number) => void;
  page: number;
  onChangePricing: (
    name: string,
    value: DISCOUNT_TIER | number | undefined
  ) => void;
  onValidateCreate: (info: CreateAccountRequest) => void;
  setAccountValidateMsg: React.Dispatch<React.SetStateAction<ValidateMsg>>;
}

export function AddModalPresenter(props: Props): JSX.Element {
  const { onChageAccountInfo, onClickCreate } = props;
  const { accountLevel } = useAppSelector((state) => state.accountSettings);
  const isDw = accountLevel === LEVEL_TYPE.DW;

  const buttons = () => {
    if (isDw) {
      if (props.page === 0) {
        return (
          <BigButton
            id="dw_account_button_create"
            label="Next"
            onClickBtn={() => props.onChangePage("next")}
            buttonType="cancel"
            data-testid="account-add-button-create-change-page"
          />
        );
      }
      return (
        <>
          <BigButton
            id="dw_account_button_create"
            label="Previous"
            onClickBtn={() => props.onChangePage("previous")}
            buttonType="cancel"
            data-testid="account-add-button-create-change-page-2"
          />

          <BigButton
            id="dw_account_button_create"
            label="Create"
            onClickBtn={() => props.onChangePage("save")}
            data-testid="account-add-button-create-change-page-3"
          />
        </>
      );
    }
    return (
      <BigButton
        id="dw_account_button_create"
        label="Create"
        delay
        onClickBtn={onClickCreate}
        disabled={props.isCreateDisabled}
        data-testid="account-add-button-create-click-create"
      />
    );
  };

  return (
    <BigPopupTemplate title="Add Organization" onModal={props.onModal}>
      <s.AddWrapper>
        {props.isCreationLoading && (
          <s.LoadingWrapper>
            <Spinner />
          </s.LoadingWrapper>
        )}

        {isDw && (
          <StepperComponent
            activeStep={props.page}
            steps={addOrgSteps}
            onClick={props.onChangePage}
            data-testid="account-add-stepper-component-change-page"
          />
        )}

        {((isDw && props.page === 0) || !isDw) && (
          <>
            <AccountInner
              accountInfo={props.accountInfo as AccountInfo}
              editAccountInfo={props.accountInfo}
              onChangeAccountInfo={onChageAccountInfo}
              isUpdateLoading={false}
              validate={props.accountValidate}
              validateMsg={props.accountValidateMsg}
              setValidateMsg={props.setAccountValidateMsg}
              isAdd
            />

            <s.OptionContainer>
              <s.OptionTitleContainer>
                Invite a new user to this organization{" "}
                {props.accountInfo.level === LEVEL_TYPE.EU && " (Optional)"}
              </s.OptionTitleContainer>
              <s.Row>
                <div style={{ display: "none" }}>
                  <ModalInput label="Name" half>
                    <Input
                      id="dw_account_input_invite_name"
                      placeholder="Please enter a name"
                      onChange={(e) =>
                        onChageAccountInfo(
                          UPDATE_TYPE.INVITE_NAME,
                          e.target.value
                        )
                      }
                      isValidated={props.accountValidate.isUserName}
                      maxLength={100}
                      autoComplete="one-time-code"
                      data-testid="account-add-input-name"
                    />

                    <ValidateWord data-testid="account-add-input-name-validate">
                      {props.accountValidateMsg.isUserName}
                    </ValidateWord>
                  </ModalInput>
                </div>
                <ModalInput
                  label="Email"
                  essential={props.accountInfo.level !== LEVEL_TYPE.EU}
                >
                  <Input
                    id="dw_account_input_invite_email"
                    placeholder="Please enter an email"
                    onChange={(e) =>
                      onChageAccountInfo(
                        UPDATE_TYPE.INVITE_EMAIL,
                        toTrim(e.target.value)
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") {
                        return;
                      }
                      if (isDw) {
                        props.onChangePage(
                          props.page === 0 ? "next" : "save"
                        );
                      } else if (!props.isCreateDisabled) {
                        onClickCreate();
                      }
                    }}
                    isValidated={props.accountValidate.isUserEmail}
                    maxLength={100}
                    autoComplete="one-time-code"
                    value={props.accountInfo.inviteEmail}
                    style={{ maxWidth: 300 }}
                    data-testid="account-add-input-email"
                  />

                  <ValidateWord data-testid="account-add-input-email-validate">
                    {props.accountValidateMsg.isUserEmail}
                  </ValidateWord>
                </ModalInput>
              </s.Row>
            </s.OptionContainer>
          </>
        )}

        {isDw && props.page === 1 && (
          <AddOrgPricing
            pricing={{ ...(props.accountInfo as PricingRequest) }}
            onChangePricing={props.onChangePricing}
            validate={props.accountValidate.discount}
            validateMsg={props.accountValidateMsg.discount}
          />
        )}

        <s.ButtonWrapper>{buttons()}</s.ButtonWrapper>
      </s.AddWrapper>
    </BigPopupTemplate>
  );
}
